import {
  AddCommentOutlined,
  AdminPanelSettings,
  BuildOutlined,
  DescriptionOutlined,
  LogoutOutlined,
  Menu as MenuIcon,
  OpenInNew,
  Person,
  TipsAndUpdates,
  VerifiedUser,
  WbIncandescentOutlined,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useClassicNavigate } from '../hooks/useClassicNavigate';
import { useGetElementSize } from '../hooks/useGetElementSize';
import { myFolder } from '../redux/consts';
import { selectAppStatus, setLLM } from '../redux/reducers/appStatusReducer';
import { selectConversations } from '../redux/reducers/conversationReducer';
import { selectFolders } from '../redux/reducers/folderReducer';
import { AppDispatch } from '../redux/store';
import { setFocusById } from '../services/util';
import { AppEnum } from '../Types/enums';
import AutoTruncatingTypographyWithTooltip from './AutoTruncatingTypographyWithTooltip';
import { CraftFeedbackWidget } from './CraftFeedbackWidget';
import FolderIcon from './Folder/FolderIcon';
import LlmSelect from './LlmSelect';
import { NotificationBell } from './NotificationBell';
import { AnalyticsEvent, useAnalytics } from './Providers/AnalyticsProvider';
import { useAuth } from './Providers/AuthProvider';
import { useFeatures } from './Providers/FeatureProvider';
import { useNavBarRoutes } from './Providers/NavbarRoutesProvider';

export enum NavBarMode {
  NIGEL = 'NIGEL',
  DEALGPT = 'DEALGPT',
  BLANK = 'BLANK',
}

export type PageRoute = {
  name: string;
  route: string;
  external?: boolean;
  tooltip?: string;
};

const IconContainer = styled('span')({
  color: 'gray',
  transition: 'all 0.2s ease-in-out',
  display: 'flex',

  span: {
    color: 'black',
    marginLeft: '5px',
  },
});

const RoutesContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  minHeight: 'inherit',
  alignItems: 'center',
});

const NavBarContainer = styled('div')({
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  width: '100%',
  minHeight: 'inherit',
  justifyContent: 'space-between',
  color: '#070154',
});

const StyledAppBar = styled(AppBar)({
  backgroundColor: 'white',
  color: 'black',
  boxShadow: '0px 2px 2px 2px rgba(16, 24, 40, 0.05)',
  zIndex: '1000',
  left: 0,
  position: 'relative',
});

const NavBarMenuItem = styled(MenuItem)({
  '&:hover': {
    outline: '1px solid #F900D3',
    backgroundColor: 'white',
  },

  '.MuiTouchRipple-child': {
    border: '1px solid rgb(244, 244, 244)',
  },

  borderRadius: '4px',
});

const ProfilePhotoButton = styled(IconButton)({
  position: 'relative',
});

const ProfilePhoto = styled('img')({
  width: '40px',
  height: '35px',
  borderRadius: '50%',
});

const AdminIcon = styled(VerifiedUser)({
  position: 'absolute',
  left: '27px',
  top: '25px',
  color: 'green',
  height: '15px',
  width: '14px',
});

const FolderTitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  justifyContent: 'flex-start',
  width: 'inherit',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
});

const releaseDate = new Date(import.meta.env.VITE_LAST_RELEASE_DATE);

const normalizedReleaseDate = new Date(
  releaseDate.getFullYear(),
  releaseDate.getMonth(),
  releaseDate.getDate()
);
const today = new Date();
const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

const twoDaysAgo = new Date(normalizedToday);
twoDaysAgo.setDate(normalizedToday.getDate() - 2);

const isWithinLastTwoDays = normalizedReleaseDate >= twoDaysAgo;

const NAVBAR_CUTOFF_WIDTH = 1150;

const NavBar = (): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const { routes, setAppMode } = useNavBarRoutes();
  const { logout, isAdmin, fetchProfilePhoto } = useAuth();
  const analytics = useAnalytics();
  const features = useFeatures();

  const { llm, selectedApp } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);
  const { currentConversation } = useSelector(selectConversations);

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [craftwidgetAnchorEl, setCraftWidgetAnchorEl] = useState<HTMLElement | null>(null);
  const [craftWidgetOpen, setCraftWidgetOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useClassicNavigate();
  const location = useLocation();
  const [profilePhoto, setProfilePhoto] = useState<string | null>(() =>
    localStorage.getItem('profilePhoto')
  );
  const { width: navBarWidth, height: navBarHeight } = useGetElementSize('navbar-container', true);
  const [navBarTooSmall, setNavBarTooSmall] = useState(navBarWidth < NAVBAR_CUTOFF_WIDTH);

  useEffect(() => {
    const updateProfilePhoto = () => {
      setProfilePhoto(localStorage.getItem('profilePhoto'));
    };

    window.addEventListener('storage', updateProfilePhoto);

    return () => {
      window.removeEventListener('storage', updateProfilePhoto);
    };
  }, []);

  useEffect(() => {
    switch (selectedApp) {
      case AppEnum.NIGEL:
        setAppMode(NavBarMode.NIGEL);
        break;
      case AppEnum.DEAL_GPT:
        setAppMode(NavBarMode.DEALGPT);
        break;
      default:
        setAppMode(NavBarMode.BLANK);
        break;
    }
  }, [selectedApp, setAppMode]);

  useEffect(() => {
    const updateNavBar = () => {
      setNavBarTooSmall(navBarWidth < NAVBAR_CUTOFF_WIDTH);
    };
    updateNavBar();

    window.addEventListener('resize', updateNavBar);

    return () => {
      window.removeEventListener('resize', updateNavBar);
    };
  }, [navBarWidth]);

  const handleOpenUserMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  }, []);

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null);
  }, []);

  const handleCraftWidgetOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      analytics.event(AnalyticsEvent.WM_NIGEL_FEEDBACK_BUTTON_CLICK);
      if (navBarTooSmall) {
        const drawer = document.getElementById('mobile-drawer');
        setCraftWidgetAnchorEl(drawer);
      } else {
        setCraftWidgetAnchorEl(event.currentTarget);
      }

      setCraftWidgetOpen(true);
    },
    [analytics, navBarTooSmall]
  );

  const handleCraftWidgetClose = useCallback(() => {
    setCraftWidgetOpen(false);
    setCraftWidgetAnchorEl(null);
  }, []);

  const handleAdminPage = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_NAVBAR_ADMIN);
    setAnchorElUser(null);
    navigate('/admin-page');
  }, [analytics, navigate]);

  const handleProfileSettings = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_NAVBAR_PROFILE_SETTINGS);
    navigate('/profile-settings');
  }, [analytics, navigate]);

  const handleReleaseNotes = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_NAVBAR_RELEASE_NOTES);
    setAnchorElUser(null);
    window.open(
      'https://howto.westmonroe.com/a/1927794-nigel-and-intellio-advantage-release-notes',
      '_blank'
    );
  }, [analytics]);

  const handleLogout = useCallback(() => {
    analytics.event(AnalyticsEvent.WM_NIGEL_NAVBAR_LOGOUT);
    setAnchorElUser(null);
    logout();
  }, [analytics, logout]);

  const handleProfilePhotoError = useCallback(() => {
    setProfilePhoto('');
    fetchProfilePhoto();
  }, [fetchProfilePhoto]);

  const toggleDrawer = useCallback(
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setDrawerOpen(open);
      document.body.style.overflow = open ? 'hidden' : 'auto'; // Prevent scrolling when the drawer is open
    },
    []
  );

  const drawerList = () => (
    <Box
      // Added a marginTop to the Mobile Right Side Nav Drawer,
      // because otherwise there would be some white space between
      // the first menu item entry and the top of the drawer
      sx={{ width: '100%', marginTop: '-8px' }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {routes.map((page) => (
          <Tooltip title={page.tooltip ?? ''} key={page.name}>
            <ListItem
              button
              key={page.name}
              onClick={() => {
                analytics.event(
                  AnalyticsEvent.WM_NIGEL_NAVBAR_ROUTE_LINKS,
                  {
                    value: page.name,
                  },
                  {
                    newPage: page.name,
                  }
                );
                if (page.external) {
                  window.open(page.route, '_blank');
                } else {
                  navigate(page.route);
                }
              }}
              sx={{
                backgroundColor:
                  navBarTooSmall && location.pathname.startsWith(page.route)
                    ? '#D3EAFF'
                    : 'inherit',
              }}
            >
              {navBarTooSmall && page.external && (
                <OpenInNew sx={{ mr: '5px', height: '20px', width: '20px' }} />
              )}
              <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: '500' }}>
                {page.name}
              </Typography>
            </ListItem>
          </Tooltip>
        ))}
      </List>
      <Divider sx={{ marginX: '10px' }} />
      <List>
        <ListItem button onClick={handleProfileSettings}>
          <Person sx={{ mr: '5px', height: '20px', width: '20px' }} />
          <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: '500' }}>
            Profile Settings
          </Typography>
        </ListItem>
        <ListItem
          button
          onClick={handleReleaseNotes}
          sx={{
            bgcolor: isWithinLastTwoDays ? '#1DD566' : undefined,
            ':hover': {
              bgcolor: isWithinLastTwoDays ? '#1DD566' : undefined,
            },
          }}
        >
          <DescriptionOutlined sx={{ mr: '5px', height: '20px', width: '20px' }} />
          <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: '500' }}>
            Release Notes
          </Typography>
        </ListItem>
        <ListItem button onClick={handleCraftWidgetOpen}>
          <TipsAndUpdates sx={{ mr: '5px', height: '20px', width: '20px' }} />
          <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: '500' }}>
            Feedback
          </Typography>
        </ListItem>
        <ListItem button onClick={handleLogout}>
          <LogoutOutlined sx={{ mr: '5px', height: '20px', width: '20px' }} />
          <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: '500' }}>
            Logout
          </Typography>
        </ListItem>
      </List>
    </Box>
  );

  const navHeaderTitle = useMemo(() => {
    if (selectedApp === AppEnum.DEAL_GPT && currentFolder?.id === myFolder.id) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'flex-start',
            width: 'inherit',
          }}
        >
          <Typography variant="h5">Intellio® Advantage</Typography>
        </div>
      );
    } else if (
      location.pathname.includes('/temporary-chat') ||
      location.pathname.includes('/search') ||
      location.pathname.includes('/pinned-chats') ||
      location.pathname.includes('/intellio-insights')
    ) {
      return null;
    } else if (currentFolder) {
      if (currentFolder.id === myFolder.id) {
        return (
          <div>
            <FolderTitle
              onClick={() => {
                analytics.event(
                  AnalyticsEvent.WM_NIGEL_FOLDER_CLICK,
                  {
                    id: currentFolder.id,
                  },
                  {
                    folderId: currentFolder.id,
                  }
                );
                navigate(`/folder/${currentFolder.id}`);
              }}
            >
              <AddCommentOutlined />
              <AutoTruncatingTypographyWithTooltip
                text={currentConversation ? currentConversation.title : 'New Chat'}
                variant="body2"
                sx={{ fontWeight: '700' }}
              />
            </FolderTitle>
          </div>
        );
      } else {
        return (
          <div>
            <FolderTitle
              onClick={() => {
                analytics.event(
                  AnalyticsEvent.WM_NIGEL_FOLDER_CLICK,
                  {
                    id: currentFolder.id,
                  },
                  {
                    folderId: currentFolder.id,
                  }
                );
                navigate(`/folder/${currentFolder.id}`);
              }}
            >
              <FolderIcon currentFolder={currentFolder} />
              <AutoTruncatingTypographyWithTooltip
                text={currentFolder.name}
                variant="body2"
                sx={{ fontWeight: '700', width: '100%' }}
              />
            </FolderTitle>
          </div>
        );
      }
    } else if (location.pathname === '/folder/create') {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'flex-start',
            width: 'inherit',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: '700' }}>
            New Folder
          </Typography>
        </div>
      );
    }
  }, [currentFolder, selectedApp, location.pathname, navigate, currentConversation, analytics]);

  return (
    <StyledAppBar id="navbar-container">
      <Container maxWidth={false}>
        <CraftFeedbackWidget
          open={craftWidgetOpen}
          setOpen={setCraftWidgetOpen}
          anchorEl={craftwidgetAnchorEl}
          handleClose={handleCraftWidgetClose}
        />
        <Toolbar disableGutters>
          <NavBarContainer>
            {navBarTooSmall ? (
              <>
                {navHeaderTitle}
                <Box
                  sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
                  id="mobile-drawer"
                >
                  {selectedApp === AppEnum.NIGEL && (
                    <LlmSelect
                      value={llm}
                      mobileNavbarLlm={true}
                      onLlmChange={(newVal) => dispatch(setLLM(newVal))}
                    />
                  )}
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: '30px', alignSelf: 'center', marginX: '10px' }}
                  />
                  <NotificationBell />
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: '30px',
                      alignSelf: 'center',
                      marginX: '10px',
                      borderRightWidth: '2px',
                    }}
                  />
                  <IconButton onClick={toggleDrawer(true)}>
                    <MenuIcon
                      sx={{
                        color: isWithinLastTwoDays ? '#1DD566' : undefined,
                        ':hover': {
                          color: isWithinLastTwoDays ? '#1DD566' : undefined,
                        },
                      }}
                    />
                  </IconButton>
                </Box>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={toggleDrawer(false)}
                  BackdropProps={{
                    invisible: true,
                  }}
                  PaperProps={{
                    style: {
                      width: '200px',
                      top: `calc(var(--banner-height, 0px) + ${navBarHeight}px)`,
                      height: `calc(100vh - var(--banner-height, 0px) - ${navBarHeight}px)`,
                      boxShadow: 'none',
                      position: 'absolute',
                    },
                  }}
                >
                  {drawerList()}
                </Drawer>
              </>
            ) : (
              <>
                <div style={{ display: 'flex' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {navHeaderTitle}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: 'fit-content',
                    justifyContent: 'flex-end',
                    gap: '8px',
                  }}
                >
                  <RoutesContainer>
                    {routes.map((page, index) => (
                      <Tooltip title={page.tooltip ?? ''} key={page.name}>
                        <NavBarMenuItem
                          id={
                            page.external
                              ? `${
                                  selectedApp === AppEnum.NIGEL
                                    ? NavBarMode.NIGEL
                                    : NavBarMode.DEALGPT
                                }-${page.name.split(' ').join('-')}`
                              : page.route.substring(1)
                          }
                          onClick={() => {
                            page.external
                              ? window.open(page.route, '_blank')
                              : navigate(page.route);
                            if (page.route.includes('/chat')) setFocusById('chat-input');
                          }}
                          selected={location.pathname.startsWith(page.route)}
                          tabIndex={index + 1}
                        >
                          <Typography
                            textAlign="center"
                            sx={{
                              fontWeight: location.pathname.startsWith(page.route) ? '500' : '400',
                              fontSize: '14px',
                            }}
                          >
                            {page.external && (
                              <OpenInNew
                                sx={{ fontSize: '20px', marginRight: '2px', verticalAlign: 'sub' }}
                              />
                            )}
                            {page.name}
                          </Typography>
                        </NavBarMenuItem>
                      </Tooltip>
                    ))}
                  </RoutesContainer>
                  <Button
                    tabIndex={1}
                    variant={isWithinLastTwoDays ? 'contained' : 'outlined'}
                    onClick={() => {
                      window.open(
                        'https://howto.westmonroe.com/a/1927794-nigel-and-intellio-advantage-release-notes',
                        '_blank'
                      );
                    }}
                    sx={{
                      borderRadius: '8px',
                      boxShadow: 'none',
                      minWidth: 'unset',
                      paddingLeft: '2px',
                      paddingRight: '5px',
                      paddingTop: '5px',
                      paddingBottom: '5px',
                      bgcolor: isWithinLastTwoDays ? '#1DD566' : undefined,
                      ':hover': {
                        bgcolor: isWithinLastTwoDays ? '#1DD566' : undefined,
                      },
                    }}
                  >
                    <DescriptionOutlined sx={{ paddingLeft: '0' }} />
                    <div style={{ marginLeft: '5px', whiteSpace: 'nowrap' }}>
                      {isWithinLastTwoDays && <b>NEW! - </b>}Release Notes
                    </div>
                  </Button>
                  <Tooltip
                    title={<Typography variant="caption"> Submit Feedback </Typography>}
                    PopperProps={{ keepMounted: true }}
                    id={'submit-feedback-button-label'}
                    sx={{ height: '40px', width: '100%' }}
                  >
                    <Button
                      aria-labelledby={'submit-feedback-button-label'}
                      tabIndex={1}
                      variant="contained"
                      onClick={handleCraftWidgetOpen}
                      sx={{
                        borderRadius: '8px',
                        boxShadow: 'none',
                        minWidth: 'unset',
                        px: '15px',
                      }}
                    >
                      <WbIncandescentOutlined sx={{ transform: 'rotate(180deg)' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: '#FFFF', marginLeft: '5px', whiteSpace: 'nowrap' }}
                      >
                        Feedback
                      </Typography>
                    </Button>
                  </Tooltip>
                  {selectedApp === AppEnum.NIGEL && (
                    <LlmSelect
                      value={llm}
                      mobileNavbarLlm={true}
                      onLlmChange={(newVal) => dispatch(setLLM(newVal))}
                    />
                  )}
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: '30px', alignSelf: 'center', marginX: '10px' }}
                  />
                  <NotificationBell />
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: '30px', alignSelf: 'center', marginX: '10px' }}
                  />
                  <Tooltip
                    title={<Typography variant="caption"> Open settings </Typography>}
                    sx={{ height: '40px', width: '40px' }}
                  >
                    <ProfilePhotoButton onClick={handleOpenUserMenu}>
                      {isAdmin && profilePhoto ? (
                        <>
                          <ProfilePhoto src={profilePhoto} alt="Open settings" />
                          <AdminIcon />
                        </>
                      ) : isAdmin && !profilePhoto ? (
                        <AdminPanelSettings />
                      ) : profilePhoto ? (
                        <ProfilePhoto
                          src={profilePhoto}
                          onError={handleProfilePhotoError}
                          alt="Profile"
                        />
                      ) : (
                        <Person />
                      )}
                    </ProfilePhotoButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <MenuItem onClick={() => handleProfileSettings()}>
                      <Typography textAlign="center">
                        <IconContainer>
                          <Person />
                          <span>Profile Settings</span>
                        </IconContainer>
                      </Typography>
                    </MenuItem>

                    {(isAdmin || features.stats_page_access) && (
                      <MenuItem onClick={() => handleAdminPage()}>
                        <Typography textAlign="center">
                          <IconContainer>
                            <BuildOutlined />
                            <span>Admin</span>
                          </IconContainer>
                        </Typography>
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => handleLogout()}>
                      <Typography textAlign="center">
                        <IconContainer>
                          <LogoutOutlined />
                          <span>Logout</span>
                        </IconContainer>
                      </Typography>
                    </MenuItem>
                  </Menu>
                </div>
              </>
            )}
          </NavBarContainer>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default NavBar;
